import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { pandaAudio as A } from "./audio";
import "./PandaParadise.css";
import { announceSpin } from "../../utils/spinBreakdown";
import { DailySuperChip } from "../../components/DailySuperMeter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PP = `${API}/games/panda-paradise/online`;
const ART = "/games/panda-paradise";
axios.defaults.withCredentials = true;

// QA-only demo controls: visible in preview (or with ?qa=1), hidden on production.
const QA = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get("qa") === "1";
    const prod = /(^|\.)betohalics\.com$/i.test(window.location.hostname);
    return q || !prod;
  } catch (_e) { return false; }
})();

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const SYM_URL = {
  WILD: `${ART}/art/PP-07_WILD.png`,
  DRAGON: `${ART}/art/PP-02_Golden_Dragon.png`,
  KOI: `${ART}/art/PP-03_Lucky_Koi.png`,
  INGOT: `${ART}/art/PP-04_Golden_Yuanbao.png`,
  JADE: `${ART}/art/PP-05_Jade_Medallion.png`,
  BAMBOO: `${ART}/art/PP-06_Bamboo.png`,
  FREE: `${ART}/ui/PP-08_Free_Play_Trigger_APPROVED_REFERENCE.png`,
  COIN: `${ART}/ui/PP-09_Gold_Value_Coin_APPROVED_REFERENCE.png`,
  A: `${ART}/art/low_A.png`, K: `${ART}/art/low_K.png`, Q: `${ART}/art/low_Q.png`,
  J: `${ART}/art/low_J.png`, TEN: `${ART}/art/low_TEN.png`, NINE: `${ART}/art/low_NINE.png`,
};
const DISPLAY_SET = ["DRAGON", "KOI", "INGOT", "JADE", "BAMBOO", "A", "K", "Q", "J", "TEN", "NINE", "WILD", "FREE"];
const randSym = () => DISPLAY_SET[(Math.random() * DISPLAY_SET.length) | 0];
// Fixed symbol run used for the smooth per-cell scroll blur during Hold & Spin.
const SPIN_STRIP = ["DRAGON", "INGOT", "BAMBOO", "K", "JADE", "KOI", "TEN", "Q", "A", "NINE"];

const TIER_META = {
  MINI: { color: "#2fd07a", label: "MINI" },
  MINOR: { color: "#3aa0ff", label: "MINOR" },
  MAJOR: { color: "#a86bff", label: "MAJOR" },
  MEGA: { color: "#ff6a2b", label: "MEGA" },
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const LINE_COLORS = ["#ffd54a", "#4ad1ff", "#ff6ad0", "#6dff8a", "#ff9a3c", "#b78bff", "#ff5a5a", "#5affe0"];

const emptyGrid = () => Array.from({ length: 5 }, () => Array.from({ length: 3 }, randSym));

export default function PandaParadise() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [balance, setBalance] = useState(0);
  const [bonus, setBonus] = useState(0);
  const [betLevels, setBetLevels] = useState([0.25, 0.5, 1, 2, 5, 10, 20]);
  const [betIdx, setBetIdx] = useState(2);
  const [jackpots, setJackpots] = useState(null);
  const [grid, setGrid] = useState(emptyGrid());
  const [spinning, setSpinning] = useState([false, false, false, false, false]);
  const [busy, setBusy] = useState(false);
  const [win, setWin] = useState(0);
  const [winLines, setWinLines] = useState([]); // winning line indices to highlight cells
  const [coinCells, setCoinCells] = useState({}); // "r-row" -> {value|tier}
  const [freeSkin, setFreeSkin] = useState(false);
  const [freeInfo, setFreeInfo] = useState(null); // {total, remaining, mult}
  const [overlay, setOverlay] = useState(null); // {type, ...}
  const [holdMode, setHoldMode] = useState(false);
  const [holdRespins, setHoldRespins] = useState(0);
  const [holdTotal, setHoldTotal] = useState(0);
  const [holdReset, setHoldReset] = useState(false);
  const [cellSpin, setCellSpin] = useState(() => new Set()); // "r-w" cells smoothly scrolling in Hold & Spin
  const [booting, setBooting] = useState(true);
  const [muted, setMuted] = useState(false);
  const [showPaytable, setShowPaytable] = useState(false);
  const [canStop, setCanStop] = useState(false);
  const [error, setError] = useState(null);

  const cycleRef = useRef(null);
  const stopRef = useRef(false);
  const mutedRef = useRef(false);
  const spinningRef = useRef([false, false, false, false, false]);
  const reelRefs = useRef([]);
  const stripRefs = useRef([]);
  const [spinData, setSpinData] = useState([[], [], [], [], []]);
  const bet = betLevels[betIdx] ?? 1;

  // ── init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const t0 = Date.now();
    const finishBoot = () => { setTimeout(() => alive && setBooting(false), Math.max(0, 2000 - (Date.now() - t0))); };
    (async () => {
      try {
        const { data: cfg } = await axios.get(`${PP}/config`);
        if (!alive) return;
        setConfig(cfg);
        const { data: st } = await axios.get(`${PP}/status`);
        if (!alive) return;
        setBalance(st.balance); setBonus(st.bonus);
        setBetLevels(st.bet_levels || cfg.bet_menu);
        setJackpots(st.jackpots?.tiers || null);
        const { data: s } = await axios.post(`${PP}/session/start`, {});
        if (!alive) return;
        setSessionId(s.session_id); setBalance(s.balance);
        finishBoot();
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load Panda Paradise.");
        finishBoot();
      }
    })();
    return () => { alive = false; A.stopMusic(); if (cycleRef.current) clearInterval(cycleRef.current); };
  }, []);

  const startCycle = () => {
    stripRefs.current.forEach((el, r) => {
      if (!el) return;
      const h = (reelRefs.current[r]?.clientHeight || 300) / 3;
      if (el._anim) el._anim.cancel();
      el.style.transform = `translateY(${-9 * h}px)`;
      el._anim = el.animate(
        [{ transform: `translateY(${-9 * h}px)` }, { transform: "translateY(0px)" }],
        { duration: 300, iterations: Infinity, easing: "linear" },
      );
    });
  };
  const stopCycle = () => {};

  // Settle one reel: final symbols sit at the TOP of the strip; scroll DOWNWARD
  // (from -travel up to 0) so symbols fall into place, then pin.
  const settleReel = (r, finalCol, settleMs = 380) =>
    new Promise((res) => {
      const h = (reelRefs.current[r]?.clientHeight || 300) / 3;
      const pad = Array.from({ length: 9 }, randSym);
      const content = [finalCol[0], finalCol[1], finalCol[2], ...pad];
      setSpinData((prev) => { const n = [...prev]; n[r] = content; return n; });
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = stripRefs.current[r];
        if (!el) { res(); return; }
        if (el._anim) el._anim.cancel();
        const travel = (content.length - 3) * h;
        el.style.transform = `translateY(${-travel}px)`;
        const anim = el.animate(
          [{ transform: `translateY(${-travel}px)` }, { transform: "translateY(0px)" }],
          { duration: settleMs, easing: "cubic-bezier(0.16,1,0.3,1)", fill: "forwards" },
        );
        anim.onfinish = () => {
          try { el.style.transform = "translateY(0px)"; anim.cancel(); } catch (_e) {}
          setGrid((g) => { const n = g.map((c) => [...c]); n[r] = [...finalCol]; return n; });
          spinningRef.current = spinningRef.current.map((v, i) => (i === r ? false : v));
          setSpinning([...spinningRef.current]);
          res();
        };
      }));
    });

  // Reveal a target 5x3 grid: fast continuous scroll, then stop reel by reel.
  // Tapping STOP mid-spin (stopRef) slams the remaining reels home fast.
  // onScatterLand() fires the instant the 3rd FREE scatter has landed.
  const revealGrid = useCallback(async (target, { anticipation = false, onScatterLand = null } = {}) => {
    stopRef.current = false;
    setCanStop(false);
    setCoinCells({}); setWinLines([]);
    spinningRef.current = [true, true, true, true, true];
    setSpinning([true, true, true, true, true]);
    setSpinData(Array.from({ length: 5 }, () => Array.from({ length: 14 }, randSym)));
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    startCycle();
    const armTimer = setTimeout(() => { if (spinningRef.current.some(Boolean)) setCanStop(true); }, 240);
    // brief lead-in spin, interruptible by STOP
    let waited = 0;
    while (waited < 340 && !stopRef.current) { await sleep(60); waited += 60; }
    let scatters = 0;
    let fireScatter = onScatterLand;
    for (let r = 0; r < 5; r++) {
      const fast = stopRef.current;
      if (!fast && anticipation && r >= 3) { A.anticipation(); await sleep(420); }
      await settleReel(r, target[r], fast ? 150 : 380);
      if (fireScatter) {
        for (const s of target[r]) if (s === "FREE") scatters++;
        if (scatters >= 3) { try { fireScatter(); } catch (_e) {} fireScatter = null; }
      }
      if (!fast) await sleep(70);
    }
    clearTimeout(armTimer);
    setCanStop(false);
    stopCycle();
    await sleep(60);
  }, []);

  const requestStop = () => { stopRef.current = true; setCanStop(false); };

  const refreshJackpots = async () => {
    try { const { data } = await axios.get(`${PP}/status`); setJackpots(data.jackpots?.tiers || null); } catch (_e) {}
  };

  // ── HOLD & SPIN (money coins) ───────────────────────────────────────────
  const runHoldSpin = async (hold, awards) => {
    const coinMap = {};
    for (const c of hold.coins) coinMap[`${c.reel}-${c.row}`] = c;
    A.trigger();
    setOverlay({ type: "hold-intro" });
    await sleep(2200);
    setOverlay(null);
    await sleep(700);  // short beat before the feature board comes alive
    setHoldMode(true);
    setHoldRespins(3);
    setHoldTotal(0);

    const locked = new Set();     // "r-w" of locked coin cells (pinned)
    const shown = {};             // coin badges by "r-w"
    const grid = Array.from({ length: 5 }, () => Array.from({ length: 3 }, () => randSym()));
    let total = 0;

    const lockCoin = (r, w) => {
      grid[r][w] = "COIN";
      locked.add(`${r}-${w}`);
      shown[`${r}-${w}`] = coinMap[`${r}-${w}`];
      const c = shown[`${r}-${w}`];
      if (c && c.type === "regular") total += c.value;
    };

    // seed the 6 triggering coins; non-locked spots keep normal symbols
    for (const [r, w] of hold.start_positions) lockCoin(r, w);
    setGrid(grid.map((c) => [...c]));
    setCoinCells({ ...shown });
    setHoldTotal(total);
    A.coin(0.85);
    await sleep(1700);

    // one respin: every reel that still has an EMPTY spot does a smooth vertical
    // scroll (same motion as the base game); locked coins land back in place and
    // any new coins lock in. Fully-locked reels stay put.
    const cycleReels = (reels) => {
      reels.forEach((r) => {
        const el = stripRefs.current[r];
        if (!el) return;
        const h = (reelRefs.current[r]?.clientHeight || 300) / 3;
        if (el._anim) el._anim.cancel();
        el.style.transform = `translateY(${-9 * h}px)`;
        el._anim = el.animate(
          [{ transform: `translateY(${-9 * h}px)` }, { transform: "translateY(0px)" }],
          { duration: 560, iterations: Infinity, easing: "linear" },
        );
      });
    };

    const doRespin = async (newCoins) => {
      const scrollReels = [];  // no locked coin + has empties -> smooth full-reel scroll
      const mixedReels = [];   // has a locked coin + empties -> coin stays frozen, spin empties only
      for (let r = 0; r < 5; r++) {
        const hasLock = [0, 1, 2].some((w) => locked.has(`${r}-${w}`));
        const hasEmpty = [0, 1, 2].some((w) => !locked.has(`${r}-${w}`));
        if (hasEmpty && !hasLock) scrollReels.push(r);
        else if (hasEmpty && hasLock) mixedReels.push(r);
      }
      const nc = new Set(newCoins.map((c) => `${c.reel}-${c.row}`));
      const target = grid.map((col, r) => col.map((_s, w) => {
        const k = `${r}-${w}`;
        return (locked.has(k) || nc.has(k)) ? "COIN" : randSym();
      }));
      // start smooth scroll on coin-free reels
      spinningRef.current = spinningRef.current.map((_v, i) => scrollReels.includes(i));
      setSpinning([...spinningRef.current]);
      setSpinData((prev) => {
        const n = [...prev];
        scrollReels.forEach((r) => { n[r] = Array.from({ length: 14 }, randSym); });
        return n;
      });
      await new Promise((rf) => requestAnimationFrame(() => requestAnimationFrame(rf)));
      cycleReels(scrollReels);
      // smooth per-cell scroll on the EMPTY cells of mixed reels (locked coins never touched)
      const spinKeys = new Set();
      for (const r of mixedReels) for (let w = 0; w < 3; w++) if (!locked.has(`${r}-${w}`)) spinKeys.add(`${r}-${w}`);
      setCellSpin(new Set(spinKeys));
      // let both the full-reel scrolls and the per-cell scrolls cruise smoothly
      await sleep(2200);
      // lock any new coins in place (permanent for the rest of the bonus)
      for (const c of newCoins) {
        locked.add(`${c.reel}-${c.row}`);
        shown[`${c.reel}-${c.row}`] = c;
        grid[c.reel][c.row] = "COIN";
        target[c.reel][c.row] = "COIN";
      }
      // land mixed-reel cells one at a time (top->bottom cascade) for a smooth stop
      const landOrder = [];
      for (const r of mixedReels) for (let w = 0; w < 3; w++) if (spinKeys.has(`${r}-${w}`)) landOrder.push([r, w]);
      for (const [r, w] of landOrder) {
        spinKeys.delete(`${r}-${w}`);
        setCellSpin(new Set(spinKeys));
        grid[r][w] = target[r][w];
        setGrid(grid.map((c) => [...c]));
        await sleep(360);
      }
      // settle scroll reels smoothly
      for (const r of scrollReels) { await settleReel(r, target[r], 760); grid[r] = [...target[r]]; await sleep(280); }
      setCoinCells({ ...shown });
      for (const c of newCoins) {
        A.coin(0.8);
        if (c.type === "regular") total += c.value;
        setHoldTotal(total);
        await sleep(460);
      }
    };

    for (const rd of hold.rounds) {
      await doRespin(rd.new_coins);
      setHoldRespins(rd.respins_left);
      if (rd.new_coins.length) {
        setHoldReset(true);
        await sleep(1200);
        setHoldReset(false);
      } else {
        await sleep(750);
      }
    }

    // jackpot / fill celebrations
    for (const aw of (awards || [])) {
      A.bigwin();
      setOverlay({ type: "jackpot", tier: aw.tier, amount: aw.amount });
      await refreshJackpots();
      await sleep(2600);
      setOverlay(null);
      total += Number(aw.amount || 0);
    }

    A.bigwin();
    setOverlay({ type: "hold-results", amount: total, filled: hold.filled });
    await sleep(2600);
    setOverlay(null);
    setHoldMode(false);
    setCoinCells({});
    setCellSpin(new Set());
    setHoldRespins(0);
    setGrid(emptyGrid()); // clear the locked-coin board back to normal reels
  };

  // ── FREE SPINS (x3) ─────────────────────────────────────────────────────
  const runFreeSpins = async (result) => {
    setOverlay({ type: "free-intro", count: result.free_spins_total, mult: result.free_mult });
    await sleep(1700);
    setOverlay(null);
    setFreeSkin(true);
    let featTotal = 0;
    const rounds = result.free_rounds || [];
    for (let i = 0; i < rounds.length; i++) {
      const rd = rounds[i];
      setFreeInfo({ total: rounds.length, remaining: rounds.length - i, mult: result.free_mult });
      await revealGrid(rd.grid);
      if (rd.line_win > 0) {
        setWinLines((rd.line_wins || []).map((w) => w.line));
        featTotal += rd.line_win; setWin(featTotal); A.win();
        await sleep(700);  // hold so the winning lines are visible
      }
      await sleep(420);
    }
    setWinLines([]);
    setFreeSkin(false);
    setFreeInfo(null);
    A.bigwin();
    setOverlay({ type: "free-results", amount: result.free_line_win });
    await sleep(2600);
    setOverlay(null);
  };

  const runFeatures = async (r, jackpots, creditedWin) => {
    if (r.hold_triggered && r.hold) await runHoldSpin(r.hold, jackpots);
    if (r.free_triggered) await runFreeSpins(r);
    if (creditedWin != null) setWin(creditedWin);
  };

  const doSpin = async () => {
    if (busy || !sessionId) return;
    setError(null);
    A.unlock(); if (!muted) A.startMusic(0.45);
    if (balance < bet) { setError("Insufficient balance."); return; }
    setBusy(true); setWin(0); setCoinCells({}); setWinLines([]); setOverlay(null);
    try {
      const { data } = await axios.post(`${PP}/spin`, { session_id: sessionId, client_request_id: uuid(), bet });
      const r = data.result;
      await revealGrid(r.base_grid, {
        anticipation: (r.free_scatters || 0) >= 2,
        onScatterLand: r.free_triggered
          ? () => { A.freeStart(); if (!mutedRef.current) A.startMusic(0.5); }
          : null,
      });
      setBalance(data.balance); setBonus(data.bonus);
      if (r.base_win > 0) {
        setWin(r.base_win);
        setWinLines((r.base_line_wins || []).map((w) => w.line));
        A.win();
      }
      await sleep(400);
      await runFeatures(r, data.jackpots || r.jackpots, data.win);
      setBalance(data.balance);
      announceSpin(bet, data.win);
      await refreshJackpots();
    } catch (e) {
      setError(e?.response?.data?.detail || "Spin failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const toggleMute = () => { const m = !muted; setMuted(m); mutedRef.current = m; A.setEnabled(!m); };

  // QA/DEMO: force a feature — no wallet change.
  const doDemo = async (kind, forceJackpot = null) => {
    if (busy) return;
    A.unlock(); if (!muted) A.startMusic(0.45);
    setError(null); setBusy(true); setWin(0); setCoinCells({}); setWinLines([]); setOverlay(null);
    try {
      const path = kind === "hold" ? "demo/hold" : "demo/free";
      const { data } = await axios.post(`${PP}/${path}`, { force_jackpot: forceJackpot });
      const r = data.result;
      await revealGrid(r.base_grid, {
        anticipation: kind === "free",
        onScatterLand: kind === "free"
          ? () => { A.freeStart(); if (!mutedRef.current) A.startMusic(0.5); }
          : null,
      });
      if (r.base_win > 0) setWin(r.base_win);
      await sleep(300);
      await runFeatures(r, data.jackpots, r.feature_win + (data.jackpots || []).reduce((s, a) => s + Number(a.amount || 0), 0));
      await refreshJackpots();
    } catch (e) {
      setError(e?.response?.data?.detail || "Demo failed.");
    } finally {
      setBusy(false);
    }
  };

  // Which cells are on a winning line (base game highlight).
  const winCells = React.useMemo(() => {
    const s = new Set();
    if (!config || !winLines.length) return s;
    for (const li of winLines) {
      const pat = config.paylines[li];
      if (pat) for (let r = 0; r < 5; r++) s.add(`${r}-${pat[r]}`);
    }
    return s;
  }, [winLines, config]);

  if (error && !config) {
    return (
      <div className="pp-root pp-error-root" data-testid="pp-root">
        <div className="pp-error-card">
          <div className="pp-error-title">Panda Paradise</div>
          <div className="pp-error-msg">{error}</div>
          <button className="pp-btn" onClick={() => navigate("/casino/slots")} data-testid="pp-back-btn">Back to Lobby</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`pp-root ${freeSkin ? "pp-free" : ""}`} data-testid="pp-root"
         style={{ backgroundImage: `url(${ART}/bg.jpg)` }}>
      <div className="pp-lanterns" aria-hidden />
      {/* Top bar: back, logo, jackpots, sound */}
      <div className="pp-topbar">
        <button className="pp-back" onClick={() => navigate("/casino/slots")} data-testid="pp-back-btn">‹ Lobby</button>
        <button className="pp-sound" onClick={toggleMute} data-testid="pp-sound-btn">{muted ? "🔇" : "🔊"}</button>
      </div>

      {QA && (
        <div className="pp-qa" data-testid="pp-qa-panel">
          <span className="pp-qa-tag">QA</span>
          <button className="pp-qa-btn" disabled={busy} onClick={() => doDemo("hold")} data-testid="pp-demo-hold-btn">▶ Demo Hold &amp; Spin</button>
          <button className="pp-qa-btn" disabled={busy} onClick={() => doDemo("hold", "MEGA")} data-testid="pp-demo-jackpot-btn">▶ Demo MEGA Coin</button>
          <button className="pp-qa-btn" disabled={busy} onClick={() => doDemo("free")} data-testid="pp-demo-free-btn">▶ Demo Free Spins</button>
        </div>
      )}

      {showPaytable && (
        <div className="pp-modal" data-testid="pp-paytable-modal" onClick={() => setShowPaytable(false)}>
          <button className="pp-modal-close" onClick={() => setShowPaytable(false)} data-testid="pp-paytable-close">×</button>
          <img className="pp-modal-img" src={`${ART}/ui/paylines.png`} alt="25 Paylines — how you win" />
        </div>
      )}

      <div className="pp-header" data-testid="pp-jackpot-strip" style={{ position: "relative" }}>
        <DailySuperChip />
        <div className="pp-jp-side">
          {["MINI", "MINOR"].map((t) => (
            <div key={t} className="pp-jp" style={{ "--jc": TIER_META[t].color }} data-testid={`pp-jackpot-${t}`}>
              <span className="pp-jp-name">{TIER_META[t].label}</span>
              <span className="pp-jp-val">{jackpots ? money(jackpots[t]?.current) : "—"}</span>
            </div>
          ))}
        </div>
        <img className="pp-logo-center" src={`${ART}/ui/PP-14_Panda_Paradise_Logo_APPROVED_REFERENCE.png`} alt="Panda Paradise" data-testid="pp-logo" />
        <div className="pp-jp-side">
          {["MAJOR", "MEGA"].map((t) => (
            <div key={t} className="pp-jp" style={{ "--jc": TIER_META[t].color }} data-testid={`pp-jackpot-${t}`}>
              <span className="pp-jp-name">{TIER_META[t].label}</span>
              <span className="pp-jp-val">{jackpots ? money(jackpots[t]?.current) : "—"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Stage: reel board floats on the deck (mascot/dragon/logo are in bg.jpg) */}
      <div className="pp-stage">
        {holdMode && (
          <>
            <div className="pp-hold-respins" data-testid="pp-hold-respins">
              <div className={`pp-hold-respins-num ${holdReset ? "pp-hold-reset" : ""}`}>{holdRespins}</div>
              <div className="pp-hold-respins-lbl">{holdReset ? "RESPINS RESET!" : "RESPINS LEFT"}</div>
            </div>
            <div className="pp-hold-total" data-testid="pp-hold-total">
              <div className="pp-hold-total-lbl">FEATURE TOTAL</div>
              <div className="pp-hold-total-val">{money(holdTotal)}</div>
            </div>
          </>
        )}
        <div className="pp-board" data-testid="pp-board">
          <div className={`pp-grid ${holdMode ? "pp-grid-hold" : ""}`}>
            {grid.map((col, r) => {
              const spin = spinning[r];
              const cells = spin ? spinData[r] : col;
              return (
                <div className="pp-reel" key={r} ref={(el) => (reelRefs.current[r] = el)}>
                  <div className={`pp-reel-strip ${spin ? "pp-reel-blur" : ""}`} ref={(el) => (stripRefs.current[r] = el)}>
                    {cells.map((sym, i) => {
                      const key = `${r}-${i}`;
                      const coin = !spin ? coinCells[key] : null;
                      const spinCell = !spin && !coin && cellSpin.has(key);
                      const blank = sym === "BLANK";
                      const symCls = sym === "WILD" ? "pp-sym-wild"
                        : sym === "COIN" ? "pp-sym-coin"
                        : sym === "FREE" ? "pp-sym-free" : "";
                      return (
                        <div className={`pp-cell ${!spin && winCells.has(key) ? "pp-cell-win" : ""} ${!spin && !spinCell ? "pp-cell-land" : ""} ${blank ? "pp-cell-blank" : ""} ${!spin && coin ? "pp-cell-coin" : ""} ${spinCell ? "pp-cellspin" : ""}`}
                             key={i} data-testid={!spin ? `pp-cell-${r}-${i}` : undefined}>
                          {spinCell ? (
                            <div className="pp-cellspin-strip"
                                 style={{ animationDuration: `${0.46 + i * 0.05}s`, animationDelay: `${r * 0.05}s` }}>
                              {[...SPIN_STRIP, ...SPIN_STRIP].map((s, k) => (
                                <div className="pp-cellspin-img" key={k}>
                                  <img src={SYM_URL[s]} alt="" draggable={false} />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <>
                              {!blank && <img className={`pp-sym ${symCls}`} src={SYM_URL[sym] || SYM_URL.NINE} alt={sym} draggable={false} />}
                              {coin && (
                                <div className={`pp-coin-badge ${coin.type === "jackpot" ? "pp-coin-jp" : ""}`}
                                     style={coin.type === "jackpot" ? { "--jc": TIER_META[coin.tier]?.color } : undefined}>
                                  {coin.type === "jackpot" ? coin.tier : money(coin.value)}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {winLines.length > 0 && !spinning.some(Boolean) && config && (
              <svg className="pp-lines-svg" viewBox="0 0 500 300" preserveAspectRatio="none" aria-hidden>
                {winLines.map((li, idx) => {
                  const pat = config.paylines[li];
                  if (!pat) return null;
                  const pts = pat.map((row, reel) => `${reel * 100 + 50},${row * 100 + 50}`).join(" ");
                  const color = LINE_COLORS[idx % LINE_COLORS.length];
                  return (
                    <polyline key={li} points={pts} fill="none" stroke={color} strokeWidth="6"
                              strokeLinejoin="round" strokeLinecap="round"
                              style={{ filter: `drop-shadow(0 0 5px ${color})`, animationDelay: `${idx * 0.08}s` }}
                              className="pp-line" />
                  );
                })}
              </svg>
            )}
          </div>
        </div>
      </div>

      {freeInfo && (
        <div className="pp-free-strip" data-testid="pp-free-badge">
          <span className="pp-free-strip-lbl">FREE SPINS LEFT</span>
          <span className="pp-free-strip-num">{freeInfo.remaining}</span>
          {freeInfo.mult > 1 && <span className="pp-free-strip-mult" data-testid="pp-free-mult">×{freeInfo.mult} ALL WINS</span>}
        </div>
      )}

      {/* HUD */}
      <div className="pp-hud">
        <div className="pp-hud-bar">
          <div className="pp-hud-icons">
            <button className="pp-hud-ico" title="How to win" onClick={() => setShowPaytable(true)} data-testid="pp-menu-btn">☰</button>
            <button className="pp-hud-ico" onClick={toggleMute} title="Sound" data-testid="pp-sound-btn2">{muted ? "🔇" : "🔊"}</button>
          </div>
          <div className="pp-meter" data-testid="pp-balance">
            <div className="pp-meter-lbl">CREDIT</div>
            <div className="pp-meter-val">{money(balance)}</div>
            {bonus > 0 && <div className="pp-bonus">bonus {money(bonus)}</div>}
          </div>
          <div className="pp-bet-box" data-testid="pp-bet">
            <div className="pp-meter-lbl">BET</div>
            <div className="pp-bet-row">
              <button className="pp-bet-btn" disabled={busy || betIdx <= 0} onClick={() => setBetIdx((i) => Math.max(0, i - 1))} data-testid="pp-bet-dec">−</button>
              <div className="pp-meter-val">{money(bet)}</div>
              <button className="pp-bet-btn" disabled={busy || betIdx >= betLevels.length - 1} onClick={() => setBetIdx((i) => Math.min(betLevels.length - 1, i + 1))} data-testid="pp-bet-inc">+</button>
            </div>
          </div>
          <div className="pp-meter" data-testid="pp-win">
            <div className="pp-meter-lbl">WIN</div>
            <div className="pp-meter-val pp-win-val">{money(win)}</div>
          </div>
          <button className="pp-maxbet" disabled={busy} onClick={() => setBetIdx(betLevels.length - 1)} data-testid="pp-maxbet-btn"><span>MAX</span><span>BET</span></button>
          <button className={`pp-spin ${busy ? (canStop ? "pp-spin-stop" : "pp-spin-busy") : ""}`}
                  onClick={busy ? requestStop : doSpin}
                  disabled={(busy && !canStop) || !sessionId} data-testid="pp-spin-btn">
            <span className="pp-spin-lbl">{busy ? (canStop ? "STOP" : "…") : "SPIN"}</span>
            <span className="pp-spin-sub">{busy ? "TAP TO STOP" : "HOLD FOR AUTOPLAY"}</span>
          </button>
        </div>
        <div className="pp-lines-caption">PLAY 25 LINES</div>
      </div>

      {error && <div className="pp-toast" data-testid="pp-error">{error}</div>}

      {/* Overlays */}
      {overlay?.type === "hold-intro" && (
        <div className="pp-overlay" data-testid="pp-hold-intro">
          <div className="pp-feature-banner pp-feature-hold">
            <div className="pp-feature-kicker">MONEY COINS</div>
            <div className="pp-feature-title">HOLD &amp; SPIN</div>
            <div className="pp-feature-sub">Coins lock · 3 respins · jackpots ride the coins</div>
          </div>
        </div>
      )}
      {overlay?.type === "hold-results" && (
        <div className="pp-overlay" data-testid="pp-hold-results">
          <div className="pp-results-card pp-results-hold">
            <div className="pp-results-lbl">{overlay.filled ? "BOARD FILLED!" : "HOLD & SPIN TOTAL"}</div>
            <div className="pp-results-amt">{money(overlay.amount)}</div>
          </div>
        </div>
      )}
      {overlay?.type === "free-intro" && (
        <div className="pp-overlay" data-testid="pp-freeplay-intro">
          <div className="pp-feature-banner pp-feature-free">
            <div className="pp-feature-kicker">PANDA BONUS</div>
            <div className="pp-feature-title">FREE SPINS</div>
            <div className="pp-free-intro-count" data-testid="pp-freeplay-count">{overlay.count}</div>
            {overlay.mult > 1 && <div className="pp-intro-mult">EVERY WIN ×{overlay.mult}</div>}
          </div>
        </div>
      )}
      {overlay?.type === "free-results" && (
        <div className="pp-overlay" data-testid="pp-freeplay-results">
          <div className="pp-results-card pp-results-free">
            <div className="pp-results-lbl">FREE SPINS TOTAL</div>
            <div className="pp-results-amt">{money(overlay.amount)}</div>
          </div>
        </div>
      )}
      {overlay?.type === "jackpot" && (
        <div className="pp-overlay pp-jp-overlay" style={{ "--jc": TIER_META[overlay.tier]?.color }} data-testid="pp-jackpot-celebration">
          <div className="pp-jp-card">
            <div className="pp-jp-title" style={{ color: TIER_META[overlay.tier]?.color }}>{overlay.tier} JACKPOT!</div>
            <div className="pp-jp-amt">{money(overlay.amount)}</div>
          </div>
        </div>
      )}

      {booting && (
        <div className="pp-loading" data-testid="pp-loading">
          <div className="pp-loading-glow" aria-hidden />
          <img className="pp-loading-logo" src={`${ART}/ui/PP-14_Panda_Paradise_Logo_APPROVED_REFERENCE.png`} alt="Panda Paradise" />
          <div className="pp-loading-bar"><span className="pp-loading-fill" /></div>
          <div className="pp-loading-powered">powered by <span>Betohalics</span></div>
        </div>
      )}
    </div>
  );
}
